"use client";

import React from "react";
import { Briefcase, Building2, Layers3, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const segments = [{
  title: "Agencies and resellers",
  subtitle: "Best fit",
  icon: Briefcase,
  description: "For agencies that want to sell branded RAG environments to multiple clients without building the retrieval, governance, and control layer themselves."
}, {
  title: "Multi-client delivery models",
  subtitle: "Strong fit",
  icon: Layers3,
  description: "For operators managing separate client environments, internal domains, or branded deployments under one structured multi-tenant model."
}, {
  title: "Multi-tenant organizations",
  subtitle: "Good fit",
  icon: Building2,
  description: "For organizations that need stronger separation, approved-only retrieval, and controlled knowledge delivery across units, entities, or environments."
}];
export default function WhoItsFor() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="who-its-for" className="bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerReveal.ref} className={cn("mx-auto mb-12 max-w-3xl text-center transition-all duration-700 ease-out", headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <ShieldCheck className="size-4" />
            <span>White-label fit</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Who white-label is built for
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            EloRAG white-label fits teams that want branded RAG delivery without giving up
            approved-only retrieval, citation-based answers, or multi-tenant control.
          </p>
        </div>

        <div ref={gridReveal.ref} className={cn("grid grid-cols-1 gap-6 md:grid-cols-3 transition-all duration-700 ease-out", gridReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {segments.map((segment, i) => {
          const Icon = segment.icon;
          return <Card key={segment.title} data-index={i} className="flex h-full flex-col rounded-2xl border border-border/70 bg-card p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)] transition-all duration-300 hover:border-primary/25 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]" style={{
            transitionDelay: `${i * 90}ms`
          }}>
                <div className="mb-5 flex items-center justify-between">
                  <div className="rounded-xl bg-primary/10 p-3">
                    <Icon className="size-5 text-primary" aria-hidden="true" />
                  </div>

                  <div className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {segment.subtitle}
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-foreground">
                  {segment.title}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {segment.description}
                </p>
              </Card>;
        })}
        </div>

        <div className="mt-12 text-center">
          <Button asChild size="lg" className="group h-12 px-8 text-base">
            <Link to="/BookADemoContactSalesPage">
              Discuss Your Use Case
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}